import PaymentTypes from "../Payments/PaymentTypes";
import PaymentProcessorsEnum from "../Payments/PaymentProcessorsEnum.js";
import {v4 as uuid} from "uuid";
import * as Localisation from "../Utils/Localisation.js";

/**
 *
 * Enables interaction with objects representing HOPS checkout payments.
 *
 * Most methods expect to work with a checkout payment object created 
 * by the `construct()` method. 
 */
class CheckoutBasketPayment {

	/**
	 * Get an payment's UI label.
	 *
	 * This is how it will be labelled in the UI (e.g. in the checkout).
	 *
	 * @param {Object} payment
	 * @return {String}
	 */
	static getUiLabel(payment) {

		const paymentType = (payment?.PaymentType || payment?.PaymentMethod?.Type);

		switch (paymentType) {
			case PaymentTypes.Card:
			case PaymentTypes.CardHolderPresent:
				return "Card";
			case PaymentTypes.Cash:
				return "Cash";
			case PaymentTypes.Voucher:
				return "Voucher";
			case PaymentTypes.CreditNote:
				return "Credit Note";
			case PaymentTypes.Cheque:
				return "Cheque";
			case PaymentTypes.Bacs:
				return "Bank Transfer";
			case PaymentTypes.Placeholder:
				return "Placeholder";
			default:
				return "Payment";
		}
	}


	/**
	 * Get an payment's UI subtext.
	 *
	 * This is extra secondary information displayed alongside it in the UI.
	 *
	 * @param {Object} payment
	 * @return {String|null}
	 */
	static getUiSubtext(payment) {

		const paymentType = (payment?.PaymentType || payment?.PaymentMethod?.Type);

		switch (paymentType) {
			case PaymentTypes.Card:
				return "Card-Not-Present";
			case PaymentTypes.Cash:
				return `Value: ${Localisation.formatCurrency(payment.Value)}, Change: ${Localisation.formatCurrency(payment.PaymentProcessorData.Change)}`;
			case PaymentTypes.CardHolderPresent:
				if (payment.PaymentHasRefunded) return "Refund Issued";
				if (payment.PaymentImmediateCharge) return "Customer has been charged";
				return "Authorised, confirm sale to charge.";
			case PaymentTypes.Voucher:
				return payment.PaymentProcessorData.Code.replace(/(.{4})(.{4})(.{4})(.{4})/, "$1-$2-$3-$4");
			default:
				return null;
		}
	}


	/**
	 * Get an payment's UI details object.
	 *
	 * These are key-value pairs of extra tertiary information to display
	 * alongside the payment in the UI generally within collapsible panels.
	 *
	 * @param {Object} payment
	 * @return {Array} Payments: Objects with `label`/value` strings
	 */
	static getUiDetails(payment) {

		const paymentType = (payment?.PaymentType || payment?.PaymentMethod?.Type);

		switch (paymentType) {
			case PaymentTypes.CardHolderPresent:
				if (payment.PaymentProcessor === PaymentProcessorsEnum.Stripe) {
					return [
						{
							label: "Transaction",
							value: "Card-Present"
						},
						{
							label: "Account Type",
							value: payment?.PaymentProcessorData?.paymentIntent?.charges?.data[0]?.payment_method_details?.card_present?.receipt?.account_type
						},
						{
							label: "Application",
							value: payment?.PaymentProcessorData?.paymentIntent?.charges?.data[0]?.payment_method_details?.card_present?.receipt?.application_preferred_name
						},
						{
							label: "AID",
							value: payment?.PaymentProcessorData?.paymentIntent?.charges?.data[0]?.payment_method_details?.card_present?.receipt?.dedicated_file_name
						},
						{
							label: "ARC",
							value: payment?.PaymentProcessorData?.paymentIntent?.charges?.data[0]?.payment_method_details?.card_present?.receipt?.authorization_response_code
						},
						{
							label: "Cryptogram",
							value: payment?.PaymentProcessorData?.paymentIntent?.charges?.data[0]?.payment_method_details?.card_present?.receipt?.application_cryptogram
						},
						{
							label: "TVR",
							value: payment?.PaymentProcessorData?.paymentIntent?.charges?.data[0]?.payment_method_details?.card_present?.receipt?.terminal_verification_results
						},
						{
							label: "TSI",
							value: payment?.PaymentProcessorData?.paymentIntent?.charges?.data[0]?.payment_method_details?.card_present?.receipt?.transaction_status_information
						}
					];
				}
				else {
					return [];
				}
			case PaymentTypes.Voucher:
				return [
					{
						label: "Available Balance",
						value: Localisation.formatCurrency(payment.PaymentProcessorData.AvailableBalance)
					},
					{
						label: "Expiry Date",
						value: Localisation.formatDateTime(payment.PaymentProcessorData.ExpiryDate)
					}
				];
			default:
				return [];
		}
	}


	/**
	 * Get an payment's receipt text.
	 *
	 * @param {Object} payment
	 * @return {String|null} `null` = don't show on receipt
	 */
	static getReceiptText(payment) {

		const paymentType = (payment?.PaymentType || payment?.PaymentMethod?.Type);

		const receiptLines = [];

		/** Card holder not present */
		if (paymentType === PaymentTypes.Card) {
			receiptLines.push(`|Card-Not-Present`);
		}

		/** In-person Card Payment */
		if (paymentType === PaymentTypes.CardHolderPresent) {

			/**
			 * Stripe Terminal Payment
			 */
			if (payment.PaymentProcessor === PaymentProcessorsEnum.Stripe) {
				receiptLines.push(
					...[
						`|Card-Present`,
						payment.PaymentHasRefunded ? `|"REFUNDED"\n|This may take 5-10 days.` : undefined,
						`|Account Type: ${payment?.PaymentProcessorData?.paymentIntent?.charges?.data[0]?.payment_method_details?.card_present?.receipt?.account_type}`,
						`|Application: ${payment?.PaymentProcessorData?.paymentIntent?.charges?.data[0]?.payment_method_details?.card_present?.receipt?.application_preferred_name}`,
						`|AID: ${payment?.PaymentProcessorData?.paymentIntent?.charges?.data[0]?.payment_method_details?.card_present?.receipt?.dedicated_file_name}`,
						`|`
					]
				);
			}
		}

		/**
		 * Voucher
		 * 
		 * We show a Voucher summary on the bottom of the receipt, so just
		 * show the Voucher code on the receipt line.
		 */
		if (paymentType === PaymentTypes.Voucher) {
			receiptLines.push([
				`|Code: ${payment?.PaymentProcessorData?.Code.replace(/(.{4})(.{4})(.{4})(.{4})/, "$1-$2-$3-$4")}`
			]);
		}

		/**
		 * MOTO
		 */
		if (payment.Moto) {
			receiptLines.push(`|Mail Order/Telephone Order (MOTO)`);
		}

		return receiptLines.length ? receiptLines.filter(i => i).join("\n") : undefined;

	}


	/**
	 * Get the data to send to the checkout APIs for a payment.
	 * https://gitlab.heron-web.com/hps/hops/-/issues/686#note_70728
	 *
	 * @param {Object} payment
	 * @return {mixed}
	 */
	static getPaymentApiData(payment) {
		switch (payment?.PaymentProcessor) {
			case PaymentProcessorsEnum.HOPS:
			case PaymentProcessorsEnum.ThirdParty:
				return {};
			case PaymentProcessorsEnum.HOPSOrderVoucher:
				return {
					OrderVoucherCode: payment?.PaymentProcessorData?.Code
				};
			case PaymentProcessorsEnum.Stripe:
				return {
					StripePaymentMethodId: payment?.PaymentProcessorData?.StripePaymentMethodId,
					StripeTerminalPaymentIntentId: payment?.PaymentProcessorData?.paymentIntent?.id
				};
			default:
				return null;
		}
	}


	/**
	 * Construct a basket payment object to represent a payment.
	 *
	 * @param {String|undefined} options.Uuid A new UUID is assigned when falsey
	 * @param {boolean} options.Moto The payment is Mail Order/Telephone Order
	 * @param {boolean} options.PaymentCanRefund The payment can be refunded without checkout
	 * @param {boolean} options.PaymentHasRefunded The payment has been refunded (before checkout)
	 * @param {boolean} options.PaymentImmediateCharge If the payment was charged immediately (i.e. not at checkout)
	 * @param {Object} options.PaymentMethod Method of payment at the organisation
	 * @param {String|undefined} options.PaymentPed A string representing the hardware device used to action the payment
	 * @param {Integer} options.PaymentProcessor Vendor-specific processor (HOPS, Stripe, Pay360, etc.)
	 * @param {mixed} options.PaymentProcessorData Data identifying this payment
	 * @param {Integer} options.PaymentType Method of payment (Cash, Card, Voucher, etc.)
	 * @param {Integer} options.TenderedAmount How much the cashier collected
	 * @param {Integer} options.Value Credit value of the payment
	 * @return {Object} Compatible as parameter to other methods in this class
	 */
	static construct({
		Uuid,
		Moto=false,
		PaymentCanRefund=false,
		PaymentHasRefunded=false,
		PaymentImmediateCharge=false,
		PaymentMethod,
		PaymentPed,
		PaymentProcessor,
		PaymentProcessorData,
		PaymentType,
		TenderedAmount,
		Value
	}) {

		if (!PaymentType && !PaymentMethod) {
			throw new Error("You must specify a payment type OR payment method");
		}

		if (PaymentType && PaymentMethod) {
			throw new Error("You cannot specify payment type AND payment method");
		}

		if (!(Value !== 0)) {
			throw new Error("Value must be non-zero.");
		}

		return {
			Uuid: (Uuid || uuid()),
			Moto,
			PaymentCanRefund,
			PaymentHasRefunded,
			PaymentImmediateCharge,
			PaymentMethod,
			PaymentPed,
			PaymentProcessor,
			PaymentProcessorData,
			PaymentType,
			TenderedAmount: (TenderedAmount || Value),
			Value
		};

	}

}

export default CheckoutBasketPayment;
